/* eslint-disable react/prop-types */
import React from 'react';
import StatusPill from '../users/StatusPill';
import { formatUTCDate } from '../../utils/utils';

const initials = (name, email) => {
  const src = (name || email || '?').trim();
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
};

const PLAN_LABEL = (plan) => (plan ? String(plan).replace(/_/g, ' ') : '—');

/**
 * User 360 identity card. Watch-connected status is a field here (this screen has no
 * population watch toggle — a single user is not a population).
 *
 * Every FIELD on it is read-only; the one action is "Grant Free Access" (065), and it is
 * optional. A caller that passes no handler gets the card exactly as it was. The card does no
 * granting itself — it hands the member back and lets the page own the modal, so this file
 * stays a view and knows nothing about the free-grants API.
 */
const IdentityCard = ({ user, onGrantAccess, onShowAllFields }) => {
  if (!user) return null;
  const platform = [user.platform, user.mobile_app_version && `v${user.mobile_app_version}`]
    .filter(Boolean).join(' · ') || '—';

  return (
    <div className="ud-idcard" data-testid="ud-identity-card">
      <div className="ud-idcard__cover" />
      <div className="ud-idcard__body">
        <div className="ud-idcard__ava" aria-hidden="true">{initials(user.name, user.email)}</div>
        <h2 className="ud-idcard__name">{user.name || '—'}</h2>
        <div className="ud-idcard__email">{user.email || '—'}</div>

        <div className="ud-meta">
          <div className="ud-meta__row">
            <span className="k">Plan</span>
            <span className="v">{PLAN_LABEL(user.subscription_plan)}</span>
          </div>
          <div className="ud-meta__row">
            <span className="k">Status</span>
            <span className="v"><StatusPill state={user.subscription_state} /></span>
          </div>
          <div className="ud-meta__row">
            <span className="k">Platform</span>
            <span className="v">{platform}</span>
          </div>
          <div className="ud-meta__row">
            <span className="k">Watch</span>
            {user.watch_connected ? (
              <span className="v" data-testid="ud-watch">{user.watch_name || 'Watch connected'}</span>
            ) : (
              <span className="v muted" data-testid="ud-watch">No watch connected</span>
            )}
          </div>
          <div className="ud-meta__row">
            <span className="k">Watch Configured</span>
            <span className="v">
              {Number(user.is_watch_connected) === 1 ? (
                <span
                  className="uv-pill uv-pill--active"
                  data-testid="ud-watch-configured"
                  data-configured="yes"
                  title="Watch configured with Pauser"
                >
                  <span className="uv-pill__dot" aria-hidden="true" />Yes
                </span>
              ) : (
                <span
                  className="uv-pill uv-pill--free"
                  data-testid="ud-watch-configured"
                  data-configured="no"
                  title="Watch not configured with Pauser"
                >
                  <span className="uv-pill__dot" aria-hidden="true" />No
                </span>
              )}
            </span>
          </div>
          <div className="ud-meta__row">
            <span className="k">Condition</span>
            {user.managing_condition ? (
              <span className="v" data-testid="ud-condition">{user.managing_condition}</span>
            ) : (
              <span className="v muted" data-testid="ud-condition">Not specified</span>
            )}
          </div>
          <div className="ud-meta__row">
            <span className="k">Joined</span>
            <span className="v">{formatUTCDate(user.created_at) || '—'}</span>
          </div>
        </div>

        {/* The rest of the users row lives in the wide "Profile details" tab — too many
            fields for this narrow card to list and stay readable. */}
        {onShowAllFields && (
          <button
            type="button"
            className="ud-allfields-link"
            onClick={onShowAllFields}
            data-testid="ud-view-all-fields"
          >
            View profile details <span aria-hidden="true">→</span>
          </button>
        )}

        {/* The same action the /users row carries, on the screen an admin is actually reading
            when they decide to give someone free access. It sits below the facts because it is
            the conclusion drawn from them, not another one of them. */}
        {onGrantAccess && (
          <div className="ud-idcard__actions">
            <button
              type="button"
              className="fg-btn fg-btn--gold ud-grant-btn"
              onClick={() => onGrantAccess(user)}
              data-testid="ud-grant-free-access"
            >
              Grant Free Access
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default IdentityCard;
